/**
 * 学习乐园 · 中国省份拼图（game-shengfen-pintu.js）
 * ============================================================
 * 「版图归位」第一作：34 个省级行政区乱序，逐块放回版图正确位置。
 * 与其他游戏同一产品底线——反馈要即时且可解释：
 *   放对了 → 说清这个省在哪（底图数据里手写的 34 条大白话）；
 *   放错了 → 告诉你落点是谁、要放的省在它哪个方向（方位由两省质心
 *   实算，不是事后编的）。
 *
 * 底图数据不在本文件：全局 STSF_MAP（shengfen-map-data.min.js，footer 按
 * config engine='shengfen' 分档先于本文件加载）。数据由
 * scripts/make-study-shengfen-map.mjs 从标准省界 GeoJSON 生成：
 * Albers 投影 + Douglas-Peucker 简化 + 整数像素量化（主图 1px ≈ 6.5km，
 * 示意轮廓；港澳 1px ≈ 0.4km 保形）。
 *
 * 合规口径（页面文案同步说明，见 doc/shengfen-pintu.html）：
 *   完整包含台湾省、香港与澳门两个特别行政区、南海诸岛十段线；
 *   港澳按地图惯例用放大框呈现（主图上放不下），南海诸岛按惯例用右下
 *   小图框呈现；轮廓为教育用简化示意，非精确边界。
 *
 * 交互（点选—点放两步，桌面移动一致，不依赖拖拽）：
 *   托盘点省名 → 选中（桌面端形状半透明跟随指针）→ 点地图落子。
 *   命中判定是环点数组上的射线法 + 3px 邻域五点采样（澳门在放大框里
 *   只有十几像素宽，单点判定容易擦边错过）。
 *
 * 简化红线（与连连看同口径，页内如实说明）：不做中断续局，刷新即重开；
 * 成绩只存本机 localStorage，不上传。
 *
 * 冒烟钩子：window.STShengfen 暴露纯引擎函数（pointInRings / hitRegion /
 * bearingWord / missExplain / shuffledIds），供冒烟跑确定性用例。
 */
(function (global) {
  'use strict';

  var MAP = global.STSF_MAP;
  var RECORD_KEY = 'study-shengfen-record';
  var HINT_MS = 2200;

  /* ============ 纯引擎（无 DOM） ============ */

  /** 射线法：点是否在环集合内（rings = [ring, …]，ring = [[x,y], …]） */
  function pointInRings(x, y, rings) {
    for (var k = 0; k < rings.length; k++) {
      var ring = rings[k];
      var inside = false;
      for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        var xi = ring[i][0], yi = ring[i][1];
        var xj = ring[j][0], yj = ring[j][1];
        if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      if (inside) return true;
    }
    return false;
  }

  function inBox(b, x, y) {
    return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
  }

  /**
   * 落点命中省份：先看特殊区域（港澳放大框 / 南海小图框整块处理），
   * 再遍历未归位省份（主图环 → 放大框环）；带 3px 邻域五点采样，
   * 兜住澳门这类十几像素宽的小目标。
   * 返回 { kind: 'region'|'sea'|'zoomempty'|'none', region? }
   */
  function hitRegion(x, y, placed) {
    if (inBox(MAP.sea.box, x, y)) return { kind: 'sea' };
    var offs = [[0, 0], [-3, 0], [3, 0], [0, -3], [0, 3]];
    var o, s, i;
    for (o = 0; o < offs.length; o++) {
      var px = x + offs[o][0], py = y + offs[o][1];
      if (inBox(MAP.zoomBox, px, py)) {
        for (i = 0; i < MAP.regions.length; i++) {
          var zr = MAP.regions[i];
          if (!placed[zr.id] && zr.zoomPoints.length && pointInRings(px, py, zr.zoomPoints)) {
            return { kind: 'region', region: zr };
          }
        }
        return { kind: 'zoomempty' };
      }
    }
    for (s = 0; s < offs.length; s++) {
      var qx = x + offs[s][0], qy = y + offs[s][1];
      for (i = 0; i < MAP.regions.length; i++) {
        var r = MAP.regions[i];
        if (placed[r.id]) continue;
        if (r.points.length && pointInRings(qx, qy, r.points)) return { kind: 'region', region: r };
      }
    }
    return { kind: 'none' };
  }

  /** 8 方位词：a 在 b 的哪个方向（统一主图坐标系 ax/ay——港澳的显示锚点
   *  在放大框坐标系里，混算方位会全错；y 向下 = 向南） */
  function bearingWord(a, b) {
    var dx = a.ax - b.ax, dy = a.ay - b.ay;
    if (Math.hypot(dx, dy) < 34) return '就在旁边';
    var deg = Math.atan2(dy, dx) / Math.PI * 180; // 0° 正东、90° 正南
    var words = ['正东', '东南', '正南', '西南', '正西', '西北', '正北', '东北'];
    return words[Math.round(((deg + 360) % 360) / 45) % 8];
  }

  /** 错放讲解：落点是谁 + 想放的省在落点的哪个方向 */
  function missExplain(sel, target) {
    var where = bearingWord(sel, target);
    if (where === '就在旁边') {
      return '这里是' + target.short + '——' + sel.short + '跟它挨得很近，再仔细看看。';
    }
    return '这里是' + target.short + '——' + sel.short + '在它的' + where + '。';
  }

  function shuffledIds(n) {
    var ids = [];
    for (var i = 0; i < n; i++) ids.push(i);
    for (var j = n - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var t = ids[j]; ids[j] = ids[k]; ids[k] = t;
    }
    return ids;
  }

  /** 环集合的包围盒（ghost 跟随与标签定位用） */
  function ringsBBox(rings) {
    var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (var k = 0; k < rings.length; k++) {
      for (var i = 0; i < rings[k].length; i++) {
        var p = rings[k][i];
        if (p[0] < x0) x0 = p[0];
        if (p[0] > x1) x1 = p[0];
        if (p[1] < y0) y0 = p[1];
        if (p[1] > y1) y1 = p[1];
      }
    }
    return { x0: x0, y0: y0, x1: x1, y1: y1 };
  }

  /* ============ 渲染与交互 ============ */

  var NS = 'http://www.w3.org/2000/svg';
  var state = {
    selected: null, placed: {}, placedCount: 0,
    miss: 0, hints: 0, elapsed: 0, timer: null, done: false,
  };
  var el = {};
  var svg, slots, labels, ghost;

  function svgEl(tag, attrs) {
    var n = document.createElementNS(NS, tag);
    for (var k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  }

  function fmtTime(ms) {
    var s = Math.floor(ms / 1000);
    return Math.floor(s / 60) + ' 分 ' + (s % 60) + ' 秒';
  }

  function say(msg, tone) {
    // 语义 → 既有状态行修饰类：ok=默认正文、bad=警示（is-warn）、done=结算（is-over）
    var TONE_CLS = { ok: '', bad: 'is-warn', done: 'is-over' };
    el.status.textContent = msg;
    el.status.className = 'st-status' + (tone && TONE_CLS[tone] ? ' ' + TONE_CLS[tone] : '');
  }

  function updateHud() {
    el.progress.textContent = '已归位 ' + state.placedCount + ' / ' + MAP.regions.length;
    el.time.textContent = '用时 ' + fmtTime(state.elapsed);
  }

  function loadRecord() {
    try { return JSON.parse(localStorage.getItem(RECORD_KEY)) || {}; }
    catch (e) { return {}; }
  }

  function renderRecord() {
    var rec = loadRecord();
    if (!rec.bestMs) {
      el.record.innerHTML = '<p class="st-map-record-empty">还没有完整归位的记录——放完第一局就有了。</p>';
      return;
    }
    el.record.innerHTML =
      '<p>最快完整归位：<strong>' + fmtTime(rec.bestMs) + '</strong>（错放 ' + rec.bestMiss + ' 次）</p>' +
      '<p class="st-map-record-sub">完整归位 ' + (rec.plays || 1) + ' 局 · 成绩只存这台设备</p>';
  }

  function saveRecord(rec) {
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 存不进就算了 */ }
  }

  /* ---- SVG 搭建：底槽 / 放大框 / 小图框 / ghost / 标注 ---- */

  function buildSvg() {
    svg = svgEl('svg', {
      viewBox: MAP.viewBox,
      class: 'st-map-svg',
      role: 'application',
      'aria-label': '中国省份拼图：把托盘里的省级行政区放回版图对应位置',
    });
    slots = {};
    labels = {};

    // 1) 主图底槽（港澳无主图 path，天然跳过）
    MAP.regions.forEach(function (r) {
      if (!r.main) return;
      var p = svgEl('path', { d: r.main, class: 'st-map-slot' });
      p.dataset.id = r.id;
      slots[r.id] = p;
      svg.appendChild(p);
    });

    // 2) 港澳放大框：主图锚点 → 引线 → 框 → 港澳槽 → 注记
    var zb = MAP.zoomBox;
    svg.appendChild(svgEl('polyline', {
      points: zb.anchor[0] + ',' + zb.anchor[1] + ' ' + (zb.x + zb.w) + ',' + (zb.y + 26) + ' ' + (zb.x + zb.w - 10) + ',' + (zb.y + 26),
      class: 'st-map-leader',
    }));
    svg.appendChild(svgEl('circle', { cx: zb.anchor[0], cy: zb.anchor[1], r: 3.5, class: 'st-map-anchor' }));
    svg.appendChild(svgEl('rect', { x: zb.x, y: zb.y, width: zb.w, height: zb.h, rx: 3, class: 'st-map-zoombox' }));
    MAP.regions.forEach(function (r) {
      if (!r.zoom) return;
      var p = svgEl('path', { d: r.zoom, class: 'st-map-slot' });
      p.dataset.id = r.id;
      slots[r.id] = p;
      svg.appendChild(p);
    });
    var zl = svgEl('text', { x: zb.x + 12, y: zb.y + 22, class: 'st-map-note' });
    zl.textContent = zb.label;
    svg.appendChild(zl);

    // 3) 南海诸岛小图框（惯例右下角）：十段线画线不填充 + 岛屿点 + 注记
    var sb = MAP.sea.box;
    svg.appendChild(svgEl('rect', { x: sb.x, y: sb.y, width: sb.w, height: sb.h, rx: 3, class: 'st-map-seabox' }));
    svg.appendChild(svgEl('path', { d: MAP.sea.lines, class: 'st-map-segline' }));
    MAP.sea.islands.forEach(function (isl) {
      var rad = Math.max(1, Math.min(3.2, Math.sqrt(isl[2]) / 9));
      svg.appendChild(svgEl('circle', { cx: isl[0], cy: isl[1], r: rad, class: 'st-map-island' }));
    });
    var sl = svgEl('text', { x: sb.x + sb.w / 2, y: sb.y + 20, class: 'st-map-note st-map-note--center' });
    sl.textContent = MAP.sea.label;
    svg.appendChild(sl);

    // 4) ghost：选中省份的半透明形状（桌面端跟随指针，触屏不出现）
    ghost = svgEl('path', { class: 'st-map-ghost', visibility: 'hidden' });
    svg.appendChild(ghost);

    // 5) 标注层：归位后逐个亮出省名
    MAP.regions.forEach(function (r) {
      var t = svgEl('text', { x: r.cx, y: r.cy, class: 'st-map-label', 'text-anchor': 'middle', visibility: 'hidden' });
      t.textContent = r.short;
      labels[r.id] = t;
      svg.appendChild(t);
    });

    el.map.innerHTML = '';
    el.map.appendChild(svg);

    // 落子与 ghost 事件绑在 svg 实例上（newGame 重建 svg 后自动随新实例生效）
    svg.addEventListener('click', onPick);
    svg.addEventListener('pointermove', onMove);
    svg.addEventListener('pointerleave', onLeave);
  }

  /** 小省标签放不下：包围盒窄的省名挪到下方 */
  function placeLabel(r) {
    var rings = r.points.length ? r.points : r.zoomPoints;
    var bb = ringsBBox(rings);
    var t = labels[r.id];
    if (bb.x1 - bb.x0 < 34 && r.points.length) {
      t.setAttribute('x', (bb.x0 + bb.x1) / 2);
      t.setAttribute('y', bb.y1 + 11);
    } else {
      t.setAttribute('x', r.cx);
      t.setAttribute('y', r.cy + 4);
    }
    t.setAttribute('visibility', 'visible');
  }

  function selectRegion(r) {
    state.selected = r;
    var btns = el.tray.querySelectorAll('[data-st-piece]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.toggle('is-active', btns[i].dataset.stPiece === r.id);
    }
    ghost.setAttribute('d', r.main || r.zoom);
    ghost.setAttribute('visibility', 'hidden'); // 指针进来再显
    var bb = ringsBBox(r.points.length ? r.points : r.zoomPoints);
    ghost.dataset.hx = String((bb.x0 + bb.x1) / 2);
    ghost.dataset.hy = String((bb.y0 + bb.y1) / 2);
    say('已选中' + r.short + '——点地图上它该在的位置；放错了我会告诉你那里是谁。');
  }

  function clearSelection() {
    state.selected = null;
    ghost.setAttribute('visibility', 'hidden');
    var btns = el.tray.querySelectorAll('[data-st-piece]');
    for (var i = 0; i < btns.length; i++) btns[i].classList.remove('is-active');
  }

  function placeCorrect(r) {
    state.placed[r.id] = true;
    state.placedCount++;
    slots[r.id].classList.add('is-placed');
    var btn = el.tray.querySelector('[data-st-piece="' + r.id + '"]');
    if (btn) {
      btn.classList.add('is-done');
      btn.disabled = true;
      btn.removeAttribute('data-st-piece');
    }
    clearSelection();
    placeLabel(r);
    updateHud();
    if (state.placedCount === MAP.regions.length) {
      finish();
    } else {
      say('对！' + r.tip + '（' + state.placedCount + '/' + MAP.regions.length + '）', 'ok');
    }
  }

  function finish() {
    state.done = true;
    stopTimer();
    var rec = loadRecord();
    var first = !rec.bestMs;
    var isNew = first || state.elapsed < rec.bestMs;
    var prevBest = rec.bestMs;
    rec.bestMs = first ? state.elapsed : Math.min(rec.bestMs, state.elapsed);
    if (isNew) rec.bestMiss = state.miss;
    rec.plays = (rec.plays || 0) + 1;
    saveRecord(rec);
    renderRecord();
    var extra = first
      ? '这是你的第一个完整归位记录！'
      : (isNew ? '新纪录！此前最快是 ' + fmtTime(prevBest) + '。' : '此前最快是 ' + fmtTime(rec.bestMs) + '。');
    say('34 个省级行政区全部归位！用时 ' + fmtTime(state.elapsed) + '，错放 ' + state.miss +
      ' 次，提示 ' + state.hints + ' 次。' + extra + '点「开始新一局」再战。', 'done');
  }

  /* ---- 指针事件 ---- */

  function toViewBox(ev) {
    var rect = svg.getBoundingClientRect();
    return [
      (ev.clientX - rect.left) / rect.width * 1000,
      (ev.clientY - rect.top) / rect.height * 800,
    ];
  }

  function onMove(ev) {
    if (state.done || !state.selected || ev.pointerType === 'touch') return;
    var p = toViewBox(ev);
    ghost.setAttribute('visibility', 'visible');
    ghost.setAttribute('transform',
      'translate(' + (p[0] - parseFloat(ghost.dataset.hx)) + ' ' + (p[1] - parseFloat(ghost.dataset.hy)) + ')');
  }

  function onLeave() {
    ghost.setAttribute('visibility', 'hidden');
  }

  /** 给提示配一句相对版图的模糊分区说明（够定位、不剧透） */
  function describeZone(r) {
    var z = r.ay < 260 ? '北方' : (r.ay > 500 ? '南方' : '中部');
    if (r.ax < 330) z += '偏西';
    else if (r.ax > 600) z += '偏东';
    return z;
  }

  function onPick(ev) {
    if (state.done) return;
    var p = toViewBox(ev);
    if (!state.selected) {
      var any = hitRegion(p[0], p[1], state.placed);
      if (any.kind === 'region') {
        say('这是' + any.region.short + '。先在托盘里挑一块，再点它该在的位置。');
      } else if (any.kind === 'sea') {
        say('右下角是南海诸岛小图框（海南省三沙市管辖），不参与拼块。');
      } else if (any.kind === 'zoomempty') {
        say('这个框是香港和澳门的放大示意——先在托盘里挑一块再放。');
      } else {
        say('先在托盘里挑一个省级行政区，再点地图放它。');
      }
      return;
    }
    var sel = state.selected;
    var hit = hitRegion(p[0], p[1], state.placed);
    if (hit.kind === 'region' && hit.region.id === sel.id) {
      placeCorrect(sel);
      return;
    }
    // —— 未命中：分口径讲清为什么 ——
    state.miss++;
    if (hit.kind === 'region' && state.placed[hit.region.id]) {
      say('这里是' + hit.region.short + '，已经归位了——' + sel.short + '在它的' + bearingWord(sel, hit.region) + '。', 'bad');
    } else if (hit.kind === 'region') {
      say(missExplain(sel, hit.region), 'bad');
    } else if (hit.kind === 'zoomempty') {
      say(sel.zoom
        ? '点在' + sel.short + '的块上——框里的空白处是内地城市示意，不算。'
        : '这个框是香港、澳门的放大示意——' + sel.short + '不在这里，往主图陆地上放。', 'bad');
    } else if (hit.kind === 'sea') {
      say('那是南海诸岛小图框，不参与拼块——' + sel.short + '在主图的' + describeZone(sel) + '。', 'bad');
    } else {
      // 主图上没命中未归位的省：可能是点了已归位的块（placed 后判定会跳过），
      // 再含归位块查一遍，如实告诉用户「这里是谁、已经归位了」
      var done = hitRegion(p[0], p[1], {});
      if (done.kind === 'region' && state.placed[done.region.id]) {
        say('这里是' + done.region.short + '，已经归位了——' + sel.short + '在它的' + bearingWord(sel, done.region) + '。', 'bad');
      } else {
        say('这里是海——省级行政区都在陆地上（台湾岛、海南岛也算「陆地」哦）。再想想' + sel.short + '该在哪。', 'bad');
      }
    }
    clearSelection();
  }

  /* ---- 托盘 / 计时 / 按钮 ---- */

  function buildTray() {
    el.tray.innerHTML = '';
    shuffledIds(MAP.regions.length).forEach(function (idx) {
      var r = MAP.regions[idx];
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'st-map-piece';
      b.dataset.stPiece = r.id;
      b.innerHTML = '<span class="st-map-piece-abbr">' + r.abbr + '</span>' + r.short;
      b.addEventListener('click', function () {
        if (state.done) return;
        if (state.selected && state.selected.id === r.id) {
          clearSelection();
          say('已取消选中。');
          return;
        }
        selectRegion(r);
      });
      el.tray.appendChild(b);
    });
  }

  function stopTimer() {
    if (state.timer) { clearInterval(state.timer); state.timer = null; }
  }

  /** 开新一局：清空归位与计数、重建托盘与版面、重新计时（口径与连连看一致：
   *  换一局就彻底换，不做「继续上局」） */
  function newGame() {
    stopTimer();
    state.placed = {};
    state.placedCount = 0;
    state.miss = 0;
    state.hints = 0;
    state.elapsed = 0;
    state.done = false;
    state.selected = null;
    buildTray();
    buildSvg();
    updateHud();
    startTimer();
    say('地图已重新洗好。从托盘挑一个省级行政区，点它该在的位置——放对了给你讲它在哪，放错了告诉你那里是谁。');
  }

  function startTimer() {
    stopTimer();
    state.timer = setInterval(function () {
      state.elapsed += 1000;
      el.time.textContent = '用时 ' + fmtTime(state.elapsed);
    }, 1000);
  }

  function onHint() {
    if (state.done) return;
    var r = state.selected;
    if (!r) {
      var left = MAP.regions.filter(function (x) { return !state.placed[x.id]; });
      if (!left.length) return;
      r = left[Math.floor(Math.random() * left.length)];
      selectRegion(r);
    }
    state.hints++;
    var slot = slots[r.id];
    slot.classList.add('is-hint');
    setTimeout(function () { slot.classList.remove('is-hint'); }, HINT_MS);
    say(r.short + '的位置在地图上闪了——它在' + describeZone(r) + '。提示 ' + state.hints + ' 次，结算会如实计入。');
  }

  function onResetRecord() {
    // 连点两次确认（口径同连连看重置成绩）
    if (el.resetBtn.dataset.armed === '1') {
      try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 忽略 */ }
      delete el.resetBtn.dataset.armed;
      el.resetBtn.textContent = '重置拼图成绩';
      renderRecord();
      say('拼图成绩已清空。');
      return;
    }
    el.resetBtn.dataset.armed = '1';
    el.resetBtn.textContent = '再点一次确认清空';
    setTimeout(function () {
      delete el.resetBtn.dataset.armed;
      el.resetBtn.textContent = '重置拼图成绩';
    }, 3000);
  }

  /* ---- 启动 ---- */

  function boot() {
    if (!MAP) return; // 数据文件未就位（加载顺序被破坏）时静默退出，不白屏
    el.map = document.querySelector('[data-st-map]');
    if (!el.map) return; // 只在拼图页生效

    el.tray = document.querySelector('[data-st-tray]');
    el.status = document.querySelector('[data-st-status]');
    el.progress = document.querySelector('[data-st-progress]');
    el.time = document.querySelector('[data-st-time]');
    el.record = document.querySelector('[data-st-record]');
    el.newBtn = document.querySelector('[data-st-new]');
    el.hintBtn = document.querySelector('[data-st-hint]');
    el.resetBtn = document.querySelector('[data-st-reset]');
    if (!el.tray || !el.status) return;

    el.newBtn.addEventListener('click', newGame);
    el.hintBtn.addEventListener('click', onHint);
    if (el.resetBtn) el.resetBtn.addEventListener('click', onResetRecord);

    // 先搭一次空场（托盘在 newGame 里重建），再开局计时
    state.placed = {};
    buildSvg();
    buildTray();
    updateHud();
    renderRecord();
    startTimer();
    say('从托盘挑一个省级行政区，点地图上它该在的位置——放对了给你讲它在哪，放错了告诉你那里是谁。');
  }

  boot();

  /* ---- 冒烟钩子（纯函数，供 scripts 下的确定性用例） ---- */
  global.STShengfen = {
    pointInRings: pointInRings,
    hitRegion: hitRegion,
    bearingWord: bearingWord,
    missExplain: missExplain,
    shuffledIds: shuffledIds,
    ringsBBox: ringsBBox,
  };
})(typeof window !== 'undefined' ? window : this);
